if (!customElements.get('kk-review-autoplay')) {
  customElements.define('kk-review-autoplay', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.controller) return;
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.widget = this.querySelector('loox-snippets-widget');
        this.toggle = this.querySelector('[data-review-toggle]');
        this.controls = this.querySelector('[data-review-controls]');
        this.previous = this.querySelector('[data-review-previous]');
        this.next = this.querySelector('[data-review-next]');
        if (!this.widget || !this.toggle) return;
        this.storageKey = `kk-review-last:${this.widget.getAttribute('product-id') || 'all'}`;
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        this.paused = this.constructor.userPaused ?? this.motion.matches;
        this.hovered = false;
        this.visible = false;
        this.toggle.hidden = false;
        this.updateControl();
        this.previous?.addEventListener('click', () => this.navigate(-1), options);
        this.next?.addEventListener('click', () => this.navigate(1), options);
        customElements.whenDefined('loox-snippets-widget').then(() => {
          if (options.signal.aborted || !this.isConnected) return;
          const root = this.widget.shadowRoot;
          if (!root) return;
          const styles = this.querySelector('[data-review-styles]');
          if (styles) root.append(styles.content.cloneNode(true));
          this.reviewObserver = new MutationObserver(() => this.syncReviews());
          this.reviewObserver.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });
          this.syncReviews();
        });
        this.toggle.addEventListener('click', () => {
          this.paused = !this.paused;
          this.constructor.userPaused = this.paused;
          this.updateControl();
          this.schedule();
        }, options);
        this.addEventListener('pointerenter', event => {
          if (event.pointerType !== 'mouse') return;
          this.hovered = true;
          this.schedule();
        }, options);
        this.addEventListener('pointerleave', () => {
          this.hovered = false;
          this.schedule();
        }, options);
        this.addEventListener('focusin', () => this.schedule(), options);
        this.addEventListener('focusout', () => queueMicrotask(() => this.schedule()), options);
        this.addEventListener('pointerdown', () => this.schedule(), options);
        this.addEventListener('click', event => {
          if (event.isTrusted) this.schedule();
        }, options);
        document.addEventListener('visibilitychange', () => this.schedule(), options);
        this.motion.addEventListener('change', event => {
          if (event.matches) this.paused = this.constructor.userPaused = true;
          this.updateControl();
          this.schedule();
        }, options);
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.5;
          this.schedule();
        }, { threshold: [0, 0.5] });
        this.observer.observe(this);
      });
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.reviewObserver?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }

    updateControl() {
      const label = this.paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel;
      this.toggle.setAttribute('aria-label', label);
      this.toggle.title = label;
      this.toggle.querySelector('[data-pause-icon]').hidden = this.paused;
      this.toggle.querySelector('[data-play-icon]').hidden = !this.paused;
    }

    updateNavigation() {
      if (!this.controls) return;
      const reviews = this.getReviews();
      this.controls.hidden = reviews.length < 2;
      this.previous.disabled = reviews.length < 2;
      this.next.disabled = reviews.length < 2;
    }

    navigate(direction) {
      const reviews = this.getReviews();
      if (reviews.length < 2) return;
      const activeIndex = reviews.findIndex(review => review.getAttribute('aria-hidden') === 'false');
      if (activeIndex < 0) return;
      const targetIndex = (activeIndex + direction + reviews.length) % reviews.length;
      if (activeIndex + direction !== targetIndex) {
        this.showReview(reviews[targetIndex].id);
      } else {
        this.widget.shadowRoot?.querySelector(direction < 0 ? '[part~="arrow-left"]' : '[part~="arrow-right"]')?.click();
      }
      this.schedule();
    }

    getReviews() {
      return Array.from(this.widget.shadowRoot?.querySelectorAll('.quickview-button[id]') || []);
    }

    shuffle(ids, avoidFirst) {
      const order = [...ids];
      for (let index = order.length - 1; index > 0; index -= 1) {
        const other = Math.floor(Math.random() * (index + 1));
        [order[index], order[other]] = [order[other], order[index]];
      }
      if (order.length > 1 && order[0] === avoidFirst) {
        [order[0], order[1]] = [order[1], order[0]];
      }
      return order;
    }

    syncReviews() {
      const reviews = this.getReviews();
      if (!reviews.length) {
        this.controls.hidden = true;
        return;
      }
      const ids = reviews.map(review => review.id);
      const key = JSON.stringify(ids);
      if (key !== this.reviewKey) {
        let last = this.currentReviewId;
        try { last ||= localStorage.getItem(this.storageKey); } catch {}
        this.remaining = this.shuffle(ids, last);
        if (!this.showReview(this.remaining[0])) return;
        this.reviewKey = key;
        this.schedule();
      } else {
        const active = reviews.find(review => review.getAttribute('aria-hidden') === 'false');
        if (active) this.rememberReview(active.id);
      }
      this.updateNavigation();
    }

    rememberReview(id) {
      this.currentReviewId = id;
      this.remaining = (this.remaining || []).filter(reviewId => reviewId !== id);
      try { localStorage.setItem(this.storageKey, id); } catch {}
    }

    showReview(id) {
      const root = this.widget.shadowRoot;
      const reviews = this.getReviews();
      const targetIndex = reviews.findIndex(review => review.id === id);
      const activeIndex = reviews.findIndex(review => review.getAttribute('aria-hidden') === 'false');
      const grid = root?.querySelector('.grid');
      if (!grid || targetIndex < 0 || activeIndex < 0) return false;
      const difference = targetIndex - activeIndex;
      const arrow = root.querySelector(difference < 0 ? '[part~="arrow-left"]' : '[part~="arrow-right"]');
      if (difference && !arrow) return false;
      const saved = ['scroll-behavior', 'scroll-snap-type'].map(property => [property, grid.style.getPropertyValue(property), grid.style.getPropertyPriority(property)]);
      try {
        grid.style.setProperty('scroll-behavior', 'auto', 'important');
        grid.style.setProperty('scroll-snap-type', 'none', 'important');
        // Native controls keep Loox's active review, keyboard access and media viewer in sync.
        for (let step = 0; step < Math.abs(difference); step += 1) arrow.click();
        grid.scrollTo({ left: reviews[targetIndex].offsetLeft - reviews[0].offsetLeft, behavior: 'instant' });
        this.rememberReview(id);
      } finally {
        for (const [property, value, priority] of saved) {
          if (value) grid.style.setProperty(property, value, priority);
          else grid.style.removeProperty(property);
        }
      }
      return true;
    }

    schedule() {
      clearTimeout(this.timer);
      const focused = this.contains(document.activeElement) && document.activeElement !== this.toggle;
      if (!this.isConnected || !this.visible || document.hidden || this.paused || this.hovered || focused) return;
      this.timer = setTimeout(() => {
        this.advance();
        this.schedule();
      }, 5000);
    }

    advance() {
      const reviews = this.getReviews();
      if (reviews.length < 2) return;
      if (!this.remaining?.length) this.remaining = this.shuffle(reviews.map(review => review.id), this.currentReviewId);
      this.showReview(this.remaining[0]);
    }
  });
}
